import type { ReactNode } from "react";
import { Drawer, Modal } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import type { DoodleBadgeId } from "@/shared/doodles/badges.data";
import { useIsMobile } from "@/shared/hooks/useIsMobile";

/**
 * The Delivery tab's side panel frame (B6 · P24, plan D-38; P25's settle panel
 * will wear it too): the design's 400px aside at the end of the screen on a
 * large screen — the shared `Drawer`, the frame every record view in the till
 * uses — and the shared bottom-sheet `Modal` below `lg`, where a 400px column
 * would cover the whole phone anyway.
 *
 * Only the frame: the caller supplies the header words, the body and the
 * footer's actions. `data-ui="delivery-panel"` scopes the PAPER rules
 * (`_paper-delivery.css`) that hold the aside at the design's 400px and split
 * its footer Cancel | primary.
 */
export function DeliverySidePanel({
  badge,
  leading,
  title,
  subtitle,
  footer,
  onClose,
  testId,
  children,
}: {
  badge?: DoodleBadgeId;
  /** Replaces the doodle badge (P25: the settle panel's initials avatar). */
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  footer: ReactNode;
  onClose: () => void;
  testId: string;
  children: ReactNode;
}) {
  const t = useTranslate();
  const mobile = useIsMobile();
  const head = leading ?? (badge ? <DoodleBadge id={badge} size={40} animate={false} /> : undefined);
  const body = (
    <div data-ui="delivery-panel" data-testid={testId} className="flex flex-col">
      {children}
    </div>
  );
  if (mobile) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        size="sm"
        headerLeading={head}
        title={title}
        subtitle={subtitle}
        closeLabel={t((m) => m.common.close)}
        footerActions={footer}
      >
        {body}
      </Modal>
    );
  }
  return (
    <Drawer
      isOpen
      onClose={onClose}
      size="sm"
      variant="flush"
      bodyTone="surface"
      headerLeading={head}
      title={title}
      subtitle={subtitle}
      closeLabel={t((m) => m.common.close)}
      footerActions={footer}
    >
      {body}
    </Drawer>
  );
}
